import { useEffect, useState, useRef, useCallback } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Admin-only dropdown menu, intended to sit next to the Sign Out button
 * in the page header. Self-hides for a user who's neither a full admin nor
 * been granted delegated access to at least one individual tool (see
 * server/services/toolAccessStore.js) — so uninvolved users never see the
 * affordance. A full admin sees every link; a delegated (non-admin) user
 * sees ONLY the specific tool(s) (`toolId`) they've been granted, plus
 * "Main app" — never "Access Control" (fullAdminOnly) or other admins' tools.
 *
 * Items are React Router navigations (no full page reload), so users can
 * jump between /admin/apps, /admin/estimation, and the main app without
 * losing context.
 */
const ADMIN_LINKS = [
    { path: '/',                  label: '🏠 Main app',          description: 'Lyric — Jira tools' },
    { path: '/admin/apps',        label: '📦 Deployed Apps',     description: 'Manage running user apps' },
    { path: '/admin/deploy-activity', label: '📜 Deploy Activity', description: 'Audit trail of every deploy, redeploy, delete, stop/start & enhance', fullAdminOnly: true },
    { path: '/admin/estimation',  label: '📏 Estimation Config', description: 'Tune AI estimator rules + team capacity' },
    { path: '/admin/repo-health', label: '🩺 Repo Health',       description: 'Schedule weekly PR-risk PDF reports by email' },
    { path: '/admin/credentials', label: '🔑 API Keys & Credentials', description: 'See every key we use, when it expires, and how to rotate it' },
    { path: '/admin/netcheck',    label: '🌐 Network Reachability', description: 'Probe whether Lyric\u2019s pod can reach a target host' },
    { path: '/admin/tester-lab',  label: '🧪 Tester Lab',         description: 'Record & replay UI test cases (sample form or a deployed app)' },
    { path: '/admin/sync',        label: '🔄 JIRA → Salesforce',  description: 'Configure & test JIRA status → Salesforce sync' },
    { path: '/admin/aws-logs',    label: '☁️ AWS Logs Access',    description: 'Test the cross-account read-only CloudWatch Logs role (OPS-4520)', fullAdminOnly: true },
    { path: '/admin/salesforce-api', label: '⚡ Salesforce API',  description: 'Test the deployed-app-facing Salesforce API + manage per-app keys', toolId: 'salesforce-api' },
    { path: '/admin/sms',         label: '💬 SMS',               description: 'Send test texts + configure the inbound-reply webhook (Twilio)', toolId: 'sms' },
    { path: '/admin/access-control', label: '🔐 Access Control',  description: 'Grant/revoke non-admins delegated access to individual tools', fullAdminOnly: true },
];

export default function AdminMenu({ style = {} }) {
    const { authState } = useOktaAuth() || {};
    const navigate = useNavigate();
    const location = useLocation();
    const [isAdmin, setIsAdmin] = useState(false);
    const [toolAccess, setToolAccess] = useState([]);
    const [email, setEmail] = useState('');
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    // One-shot fetch on auth. We refetch on auth changes so we don't keep
    // a stale isAdmin=true if the user signs out and a non-admin signs in.
    useEffect(() => {
        let cancelled = false;
        if (!authState?.isAuthenticated) {
            setIsAdmin(false);
            setToolAccess([]);
            return;
        }
        (async () => {
            try {
                const token = authState?.accessToken?.accessToken;
                const res = await fetch('/api/me/admin-status', {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                const body = await res.json().catch(() => ({}));
                if (!cancelled && res.ok) {
                    setIsAdmin(!!body.isAdmin);
                    setToolAccess(Array.isArray(body.toolAccess) ? body.toolAccess : []);
                    setEmail(body.email || '');
                }
            } catch { /* network / 401 — leave isAdmin false; menu stays hidden */ }
        })();
        return () => { cancelled = true; };
    }, [authState]);

    // Close on outside click — typical dropdown behavior.
    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    const go = useCallback((path) => {
        setOpen(false);
        if (location.pathname !== path) navigate(path);
    }, [navigate, location.pathname]);

    // A link is visible if: it's unrestricted (Main app), the caller is a
    // full admin (sees everything), or it's a delegated tool the caller has
    // been specifically granted (never "fullAdminOnly" ones like Access
    // Control).
    const visibleLinks = ADMIN_LINKS.filter((link) => {
        if (isAdmin) return true;
        if (link.fullAdminOnly) return false;
        if (!link.toolId) return link.path === '/';
        return toolAccess.includes(link.toolId);
    });

    if (!isAdmin && visibleLinks.length <= 1) return null; // only "Main app" (or nothing) to show

    return (
        <div ref={rootRef} style={{ ...styles.container, ...style }}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                style={open ? { ...styles.trigger, ...styles.triggerOpen } : styles.trigger}
                onMouseEnter={(e) => { if (!open) e.currentTarget.style.background = '#33334a'; }}
                onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = '#2a2a3a'; }}
                title={email ? `Admin menu — ${email}` : 'Admin menu'}
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <span style={{ fontSize: '14px' }}>⚙</span>
                <span>Admin</span>
                <span style={styles.triggerCaret}>{open ? '▴' : '▾'}</span>
            </button>

            {open && (
                <div role="menu" style={styles.menu}>
                    {email && (
                        <div style={styles.headerRow}>
                            <div style={styles.headerLabel}>Signed in</div>
                            <div style={styles.headerEmail}>{email}</div>
                        </div>
                    )}
                    {visibleLinks.map(link => {
                        const active = location.pathname === link.path
                            || (link.path !== '/' && location.pathname.startsWith(link.path));
                        return (
                            <button
                                key={link.path}
                                role="menuitem"
                                type="button"
                                onClick={() => go(link.path)}
                                style={active ? { ...styles.item, ...styles.itemActive } : styles.item}
                                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = '#2a2a3a'; }}
                                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                            >
                                <div style={styles.itemLabel}>
                                    {link.label}
                                    {active && <span style={styles.itemBadge}>Current</span>}
                                </div>
                                <div style={styles.itemDesc}>{link.description}</div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// Neutral palette so the trigger looks at home on both the landing page
// (light card on dark bg) and the admin pages (dark slate). Hover/active
// states are handled via inline onMouseEnter/Leave on the trigger.
const styles = {
    container: { position: 'relative', display: 'inline-block', fontFamily: 'ui-sans-serif, system-ui, -apple-system, sans-serif' },
    trigger: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '7px 14px',
        fontSize: '13px',
        lineHeight: 1.2,
        background: '#2a2a3a',
        color: '#e5e7eb',
        border: '1px solid #3f3f55',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: 600,
        transition: 'background-color 120ms ease, border-color 120ms ease',
        userSelect: 'none',
        whiteSpace: 'nowrap',
    },
    triggerOpen: {
        background: '#3a3a4f',
        borderColor: '#5b5b78',
    },
    triggerCaret: { fontSize: '9px', color: '#9ca3af', marginLeft: '2px' },
    menu: {
        position: 'absolute',
        top: 'calc(100% + 8px)',
        right: 0,
        minWidth: '280px',
        background: '#1c1c28',
        border: '1px solid #2e2e42',
        borderRadius: '8px',
        boxShadow: '0 12px 32px rgba(0,0,0,0.45), 0 2px 6px rgba(0,0,0,0.3)',
        padding: '4px',
        zIndex: 100,
        overflow: 'hidden',
    },
    headerRow: {
        padding: '10px 12px 8px',
        borderBottom: '1px solid #2e2e42',
        marginBottom: '4px',
    },
    headerLabel: {
        fontSize: '10px',
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontWeight: 600,
    },
    headerEmail: {
        fontSize: '13px',
        color: '#e5e7eb',
        wordBreak: 'break-all',
        marginTop: '2px',
    },
    item: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
        textAlign: 'left',
        padding: '8px 12px',
        background: 'transparent',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        color: '#e5e7eb',
        transition: 'background-color 100ms ease',
    },
    itemActive: {
        background: '#1e3a5f',
    },
    itemHover: {
        background: '#2a2a3a',
    },
    itemLabel: { fontSize: '13px', fontWeight: 600, color: '#f3f4f6' },
    itemDesc: { fontSize: '11px', color: '#9ca3af', marginTop: '2px' },
    itemBadge: {
        fontSize: '9px',
        marginLeft: '6px',
        padding: '1px 6px',
        borderRadius: '999px',
        background: '#3a5f9c',
        color: '#dbeafe',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        fontWeight: 700,
    },
};
